import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Card, Modal, message, Popconfirm, Row, Col, Input, Space } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';

import FaqForm from '../components/FaqForm';
import { getFaqs, createFaq, updateFaq, deleteFaq } from '../utils/apiClient';
import { AppSelector } from '../components/AppSelector';

const Faqs = () => {
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchText) params.search = searchText;
      if (categoryFilter) params.category = categoryFilter;
      const res = await getFaqs(params);
      setData(res.data);
    } catch (err) {
      message.error('Failed to fetch faqs');
    }
    setLoading(false);
  }, [searchText, categoryFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    { title: 'App', dataIndex: 'category', key: 'category' },
    { title: 'Question', dataIndex: 'question', key: 'question' },
    { title: 'Tags', dataIndex: 'tags', key: 'tags' },
    { title: 'Answer', dataIndex: 'answer', key: 'answer' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <>
          <Button type="link" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm title="Delete this faq?" onConfirm={() => handleDelete(record._id)}>
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingItem(null);
    setModalVisible(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingItem(null);
  };

  const handleFormSubmit = async (values) => {
    try {
      if (editingItem) {
        await updateFaq(editingItem._id, values);
        message.success('Faq updated');
      } else {
        await createFaq(values);
        message.success('Faq created');
      }
      fetchData();
      handleModalClose();
    } catch {
      message.error('Save failed');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteFaq(id);
      message.success('Faq deleted');
      fetchData();
    } catch {
      message.error('Delete failed');
    }
  };
  const handleSearch = () => {
    fetchData();
  };

  const handleReset = () => {
    setSearchText('');
    setCategoryFilter('');
    fetchData();
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <h2 style={{ margin: 0 }}>Faqs</h2>
          <div>
            <Button type="primary" onClick={handleAdd} style={{ marginRight: 8 }}>
              Add Faq
            </Button>
          </div>
        </div>

        <div className="table-actions">
          <Row gutter={[16, 16]} style={{ width: '100%' }}>
            <Col xs={24} sm={8} md={6}>
              <Input
                placeholder="Search..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={handleSearch}
              />
            </Col>
            <Col xs={24} sm={8} md={4}>
              <AppSelector value={categoryFilter || undefined} onChange={setCategoryFilter} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Space>
                <Button type="primary" onClick={handleSearch}>
                  Search
                </Button>
                <Button onClick={handleReset}>Reset</Button>
                <Button icon={<ReloadOutlined />} onClick={() => fetchData()} title="Refresh" />
              </Space>
            </Col>
          </Row>
        </div>
        <Table rowKey="_id" columns={columns} dataSource={data} loading={loading} />
        <Modal
          open={modalVisible}
          onCancel={handleModalClose}
          footer={null}
          destroyOnClose
          title={editingItem ? 'Edit Faq' : 'Add Faq'}
        >
          <FaqForm item={editingItem} onClose={handleModalClose} onSubmit={handleFormSubmit} />
        </Modal>
      </Card>
    </div>
  );
};

export default Faqs;

